import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { colors, spacing, fontSize, radius, shadow } from '../styles/theme'

interface TooltipProps {
  children: React.ReactNode
  text?: string
  content?: React.ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  /**
   * When true, tooltip is rendered in document.body with position:fixed
   * (escapes overflow:hidden ancestors). Position is derived from the
   * trigger element's bounding rect.
   */
  portal?: boolean
  /** When true, tooltip follows the cursor. Uses a document portal; ignores position. */
  anchorToCursor?: boolean
  /** Pixels to the right of the cursor when anchorToCursor is true. */
  cursorOffsetX?: number
  show?: boolean
  minWidth?: string | number
  maxWidth?: string | number
  backgroundColor?: string
  horizontalOffset?: number
  verticalOffset?: number
}

const SPACING_PX = parseInt(spacing.xs) || 4

const TooltipComponent: React.FC<TooltipProps> = ({
  children,
  text,
  content,
  position = 'top',
  portal = false,
  anchorToCursor = false,
  cursorOffsetX = 12,
  show = false,
  minWidth,
  maxWidth,
  backgroundColor,
  horizontalOffset = 0,
  verticalOffset = 0,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [cursor, setCursor] = useState({ x: 0, y: 0 })

  const handlePointerMove = useCallback(
    (e: React.MouseEvent) => {
      if (anchorToCursor && show) setCursor({ x: e.clientX, y: e.clientY })
    },
    [anchorToCursor, show]
  )

  const handlePointerEnter = useCallback(
    (e: React.MouseEvent) => {
      if (anchorToCursor) setCursor({ x: e.clientX, y: e.clientY })
    },
    [anchorToCursor]
  )

  const [anchorPortalBox, setAnchorPortalBox] = useState<React.CSSProperties | null>(null)

  const positionAnchorPortal = useCallback(() => {
    if (!show || !portal || anchorToCursor) { setAnchorPortalBox(null); return }
    const el = anchorRef.current
    if (!el) { setAnchorPortalBox(null); return }
    const rect = el.getBoundingClientRect()
    const h = horizontalOffset
    const v = verticalOffset
    const base: React.CSSProperties = {
      position: 'fixed',
      zIndex: 10002,
      minWidth: minWidth || (content ? '150px' : 'auto'),
      maxWidth: maxWidth || (content ? '300px' : 'none'),
      width: 'max-content',
      pointerEvents: 'none',
    }
    switch (position) {
      case 'right':
        setAnchorPortalBox({ ...base, left: rect.right + SPACING_PX + h, top: rect.top + rect.height / 2 + v, transform: 'translateY(-50%)' })
        break
      case 'left':
        setAnchorPortalBox({ ...base, left: rect.left - SPACING_PX + h, top: rect.top + rect.height / 2 + v, transform: 'translate(-100%, -50%)' })
        break
      case 'top':
        setAnchorPortalBox({ ...base, left: rect.left + rect.width / 2 + h, top: rect.top - SPACING_PX + v, transform: 'translate(-50%, -100%)' })
        break
      case 'bottom':
        setAnchorPortalBox({ ...base, left: rect.left + rect.width / 2 + h, top: rect.bottom + SPACING_PX + v, transform: 'translate(-50%, 0)' })
        break
      default:
        setAnchorPortalBox(null)
    }
  }, [show, portal, anchorToCursor, position, horizontalOffset, verticalOffset, minWidth, maxWidth, content])

  useLayoutEffect(() => { positionAnchorPortal() }, [positionAnchorPortal])

  useEffect(() => {
    if (!show || !portal || anchorToCursor) return
    positionAnchorPortal()
    window.addEventListener('scroll', positionAnchorPortal, true)
    window.addEventListener('resize', positionAnchorPortal)
    return () => {
      window.removeEventListener('scroll', positionAnchorPortal, true)
      window.removeEventListener('resize', positionAnchorPortal)
    }
  }, [show, portal, anchorToCursor, positionAnchorPortal])

  const bgColor = backgroundColor || colors.surface

  const getArrowStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = { position: 'absolute', width: 0, height: 0 }
    switch (position) {
      case 'bottom': return { ...base, bottom: '100%', left: '50%', transform: 'translateX(-50%)', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: `6px solid ${bgColor}` }
      case 'right':  return { ...base, right: '100%', top: '50%', transform: 'translateY(-50%)', borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderRight: `6px solid ${bgColor}` }
      case 'left':   return { ...base, left: '100%', top: '50%', transform: 'translateY(-50%)', borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: `6px solid ${bgColor}` }
      default:       return { ...base, top: '100%', left: '50%', transform: 'translateX(-50%)', borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `6px solid ${bgColor}` }
    }
  }

  const getTooltipPosition = (): React.CSSProperties => {
    const h = horizontalOffset
    const v = verticalOffset
    switch (position) {
      case 'top':    return { position: 'absolute', bottom: '100%', left: '50%', transform: `translate(calc(-50% + ${h}px), ${-v}px)`, marginBottom: `${SPACING_PX}px` }
      case 'bottom': return { position: 'absolute', top: '100%', left: '50%', transform: `translate(calc(-50% + ${h}px), ${v}px)`, marginTop: `${SPACING_PX}px` }
      case 'left':   return { position: 'absolute', right: '100%', top: '50%', transform: `translate(${-h}px, calc(-50% + ${v}px))`, marginRight: `${SPACING_PX}px` }
      case 'right':  return { position: 'absolute', left: '100%', top: '50%', transform: `translate(${h}px, calc(-50% + ${v}px))`, marginLeft: `${SPACING_PX}px` }
    }
  }

  const wrapperStyle: React.CSSProperties = { position: 'relative' }
  let needsBlock = false
  try {
    const child = React.Children.only(children) as React.ReactElement<{ style?: React.CSSProperties }>
    const s = child?.props?.style || {}
    needsBlock = s.width === '100%' || s.display === 'block'
  } catch { /* keep inline-block */ }

  if (needsBlock) {
    wrapperStyle.display = 'block'
    wrapperStyle.width = '100%'
    wrapperStyle.height = '100%'
  } else {
    wrapperStyle.display = 'inline-block'
  }

  const renderText = () => {
    if (content) return content
    if (!text) return null
    const hasNewline = text.includes('\n') || text.includes('\\n')
    if (hasNewline) {
      return text.replace(/\\n/g, '\n').split('\n').map((line, i, arr) => (
        <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
      ))
    }
    return text
  }

  const innerBoxStyle: React.CSSProperties = {
    position: 'relative',
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: bgColor,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    boxShadow: shadow.lg,
    fontSize: fontSize.sm,
    color: colors.text,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    pointerEvents: 'none',
    width: '100%',
    boxSizing: 'border-box',
  }

  const cursorArrowStyle: React.CSSProperties = {
    position: 'absolute',
    left: -6,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '6px solid transparent',
    borderBottom: '6px solid transparent',
    borderRight: `6px solid ${bgColor}`,
  }

  const cursorPortal = anchorToCursor && show && typeof document !== 'undefined'
    ? createPortal(
        <div style={{ position: 'fixed', left: cursor.x + cursorOffsetX + horizontalOffset, top: cursor.y + verticalOffset, transform: 'translateY(-50%)', zIndex: 10002, minWidth: minWidth || (content ? '150px' : 'auto'), maxWidth: maxWidth || (content ? '300px' : 'none'), width: 'max-content', pointerEvents: 'none' }}>
          <div style={innerBoxStyle}>{renderText()}<div style={cursorArrowStyle} /></div>
        </div>,
        document.body
      )
    : null

  const anchorPortal = portal && show && !anchorToCursor && anchorPortalBox && typeof document !== 'undefined'
    ? createPortal(
        <div style={anchorPortalBox}>
          <div style={innerBoxStyle}>{renderText()}<div style={getArrowStyle()} /></div>
        </div>,
        document.body
      )
    : null

  return (
    <div
      ref={anchorRef}
      style={wrapperStyle}
      onMouseMove={anchorToCursor ? handlePointerMove : undefined}
      onMouseEnter={anchorToCursor ? handlePointerEnter : portal ? positionAnchorPortal : undefined}
    >
      {children}
      {show && !anchorToCursor && !portal && (
        <div style={{ position: 'absolute', ...getTooltipPosition(), zIndex: 10001, minWidth: minWidth || (content ? '150px' : 'auto'), maxWidth: maxWidth || (content ? '300px' : 'none'), width: 'max-content' }}>
          <div style={innerBoxStyle}>{renderText()}<div style={getArrowStyle()} /></div>
        </div>
      )}
      {anchorPortal}
      {cursorPortal}
    </div>
  )
}

export const Tooltip = React.memo(TooltipComponent)
